import React from 'react';
import { StatusType } from '@api/models/prayer_hub/interface';
import { Params as FormValues, createSchema as createHubSchema } from '@api/routes/prayer-hubs/create/params';
import { POINTS_AWARD } from '@api/utils/gamification';
import cn from 'classnames';
import { Field, Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import Lottie from 'lottie-react';
import { Link, useNavigate } from 'react-router-dom';
import createBg from 'src/assets/create-page-bg.svg';
import logoLottie from 'src/assets/logo-lottie.json';
import MediaUploaderSimple from 'src/components/common/Form/MediaUploaderSimple';
import TextField from 'src/components/common/Form/TextField';
import Header from 'src/components/common/Header';
import Icon from 'src/components/common/Icon';
import { Loader } from 'src/components/common/Loader';
import { ROOT } from 'src/constants/routes';
import { useTranslation } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { prayerHubs as prayerHubsApi } from 'src/store/api/prayer-hubs';
import { updatePoints } from 'src/store/user';
import { selectUserId } from 'src/store/user/selectors';

import { BotMessage, BotThinkingIndicator } from './BotComponents';
import css from './CreateHub.module.scss';
import { UserMediaMessage, UserTextMessage } from './UserMessages';
import { createBotMessage, createUserMessage } from './helpers';
import { ChatMessage, ChatSteps, MediaType, UserMessagePayload } from './types';

// const createHubSchema = createCampaignSchema.omit(['confirmation']);

const CHAT_STEPS = [
  {
    step: ChatSteps.personName,
    message: 'botStepMessages.personName',
    formikFieldType: 'text',
    formikField: 'personName',
  },
  {
    step: ChatSteps.gender,
    message: 'botStepMessages.gender',
    formikFieldType: 'radio',
    formikField: 'gender',
  },
  {
    step: ChatSteps.motherName,
    message: 'botStepMessages.motherName',
    formikFieldType: 'text',
    formikField: 'motherName',
  },
  {
    step: ChatSteps.description,
    message: 'botStepMessages.description',
    formikFieldType: 'text',
    formikField: 'description',
  },
  {
    step: ChatSteps.image,
    message: 'botStepMessages.image',
    formikFieldType: 'media',
    formikField: 'image',
  },
  {
    step: ChatSteps.video,
    message: 'botStepMessages.video',
    formikFieldType: 'media',
    formikField: 'video',
  },
  {
    step: ChatSteps.confirmation,
    message: 'botStepMessages.confirmation',
    formikField: null,
  },
  {
    step: ChatSteps.loader,
    message: 'botStepMessages.loader',
    formikField: null,
  },
  {
    step: ChatSteps.final,
    message: 'botStepMessages.final',
    formikField: null,
  },
];

const initialValues: FormValues = {
  personName: '',
  motherName: '',
  description: '',
  status: StatusType.pending,
  category: '',
  encouragementMessage: '',
  prayer: '',
  video: '',
  image: '',
  psalmsChapter: '',
  shareLinks: {},
  gender: 'male',
  isPinned: false,
};

const BOT_THINKING_DELAY_MS = 1000;
const BOT_THINKING_JITTER_MS = 350;

export default function CreateHub() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const { translate: _t } = useTranslation('createHub');
  const [createHub] = prayerHubsApi.endpoints.createHub.useMutation();

  const [step, setStep] = React.useState<ChatSteps>(ChatSteps.personName);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [isBotThinking, setIsBotThinking] = React.useState(true);
  const [isBotTypingDone, setIsBotTypingDone] = React.useState(false);

  const thinkingTimeoutRef = React.useRef<number | null>(null);
  const chatEndRef = React.useRef<HTMLDivElement>(null);

  const currentStepIndex = CHAT_STEPS.findIndex(item => item.step === step);
  const currentStep = CHAT_STEPS[currentStepIndex];

  React.useEffect(() => {
    return () => {
      if (thinkingTimeoutRef.current !== null) {
        window.clearTimeout(thinkingTimeoutRef.current);
      }
    };
  }, []);

  const scrollChatToEnd = React.useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  React.useEffect(() => {
    scrollChatToEnd();
  }, [messages, step, isBotThinking, isBotTypingDone, scrollChatToEnd]);

  React.useEffect(() => {
    setIsBotThinking(true);
    setIsBotTypingDone(false);

    if (thinkingTimeoutRef.current !== null) {
      window.clearTimeout(thinkingTimeoutRef.current);
    }

    const delay = BOT_THINKING_DELAY_MS + Math.floor(Math.random() * BOT_THINKING_JITTER_MS);
    thinkingTimeoutRef.current = window.setTimeout(() => {
      thinkingTimeoutRef.current = null;
      setIsBotThinking(false);
    }, delay);
  }, [step]);

  const archiveCurrentExchange = (userAnswer: UserMessagePayload) => {
    setMessages(prev => [...prev, createBotMessage(_t(currentStep.message)), createUserMessage(userAnswer)]);
  };

  const handleMediaUpload = async (mediaType: MediaType, url: string, fileName: string) => {
    archiveCurrentExchange({
      type: 'media',
      mediaType,
      url,
      fileName,
    });

    goNextStep();
  };

  const goNextStep = () => {
    const nextStep = CHAT_STEPS[currentStepIndex + 1];

    if (nextStep) {
      setStep(nextStep.step);
    }
  };

  const handleSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    try {
      setStep(ChatSteps.loader);

      await createHub({ ...values }).unwrap();

      dispatch(updatePoints(POINTS_AWARD.HUB_CREATED));

      setStep(ChatSteps.final);
    } catch (e) {
      console.error(e);
      setMessages(prev => (prev.length >= 2 ? prev.slice(0, -2) : prev));
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const validateStep = async (
    fieldName: string,
    values: FormValues,
    setFieldTouched: FormikProps<FormValues>['setFieldTouched'],
    setFieldError: FormikProps<FormValues>['setFieldError']
  ) => {
    await setFieldTouched(fieldName, true, false);

    try {
      await createHubSchema.validateAt(fieldName, values);

      setFieldError(fieldName, undefined);
      return true;
    } catch (yupError: any) {
      const errorMessage = yupError.message || 'Invalid value';

      setFieldError(fieldName, errorMessage);
      return false;
    }
  };

  const renderFormikField = (
    type: string | undefined,
    setFieldValue: (
      field: string,
      value: unknown,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<FormValues>>,
    values: FormValues,
    isBotBusy: boolean
  ) => {
    switch (type) {
      case 'text': {
        const fieldName = currentStep.formikField as 'personName' | 'motherName' | 'description';

        if (fieldName === 'description') {
          return (
            <TextField
              name="description"
              placeholder={_t('form.textFieldPlaceholder')}
              maxChars={200}
              multiline
              rows={6}
              isRequired
            />
          );
        }

        return <TextField name={fieldName} placeholder={_t('form.textFieldPlaceholder')} isRequired />;
      }

      case 'media':
        return (
          <div className={css.media}>
            <MediaUploaderSimple
              name={currentStep.formikField as MediaType}
              type={currentStep.formikField === 'image' ? 'images' : 'videos'}
              label={currentStep.formikField === 'image' ? _t('form.uploadImageBtn') : _t('form.uploadVideoBtn')}
              userId={userId}
              onUploadComplete={(url, fileName) =>
                void handleMediaUpload(currentStep.formikField as MediaType, url, fileName)
              }
            />

            <button
              type="button"
              className={css.skipBtn}
              disabled={isBotBusy}
              onClick={async () => {
                await setFieldValue(currentStep.formikField as keyof FormValues, '');
                archiveCurrentExchange({ type: 'text', text: _t('buttons.skip') });
                goNextStep();
              }}
            >
              {_t('buttons.skip')}
            </button>
          </div>
        );

      case 'radio':
        return (
          <div className={css.radio}>
            <label className={cn(css.radioBtn, values.gender === 'male' && css.isActive)}>
              <Field type="radio" name={currentStep.formikField} value="male" />
              {_t('buttons.male')}
            </label>
            <label className={cn(css.radioBtn, values.gender === 'female' && css.isActive)}>
              <Field type="radio" name={currentStep.formikField} value="female" />
              {_t('buttons.female')}
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  const renderFinalStep = () => {
    return (
      <div className={css.final}>
        <div className={css.finalContent}>
          <Lottie animationData={logoLottie} loop autoplay />
          <div className={css.finalText} dangerouslySetInnerHTML={{ __html: _t(currentStep.message) }}></div>
          <button type="button" className={css.confirmBtn} onClick={() => navigate(ROOT)}>
            {_t('buttons.toMain')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={css.create} style={{ backgroundImage: `url(${createBg})` }}>
      <Header className={css.header} hideLogo />
      <Link to={ROOT} className={css.backBtn} aria-label="חזרה">
        <Icon type="arrow-left" />
      </Link>

      {step === ChatSteps.final ? (
        renderFinalStep()
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={createHubSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldTouched, setFieldValue, setFieldError, submitForm, isSubmitting }) => {
            const isBotBusy = isBotThinking || !isBotTypingDone;

            return (
              <Form
                className={css.form}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent entire form validation

                    const field = currentStep.formikField;

                    if (!field || field === 'media') {
                      return;
                    }

                    const isValid = await validateStep(field, values, setFieldTouched, setFieldError);

                    if (!isValid) {
                      return;
                    }

                    archiveCurrentExchange({
                      type: 'text',
                      text:
                        currentStep.formikFieldType === 'radio'
                          ? _t(`buttons.${values.gender}`)
                          : String(values[field as keyof FormValues] ?? '').trim(),
                    });
                    goNextStep();
                  }
                }}
              >
                <div className={css.chatMessages}>
                  {messages.map(message => {
                    if (message.role === 'bot') {
                      return <BotMessage key={message.id} text={message.text} />;
                    }

                    if (message.type === 'media') {
                      return (
                        <UserMediaMessage
                          key={message.id}
                          fieldName={message.mediaType === 'image' ? 'image' : 'video'}
                          mediaType={message.mediaType}
                          url={message.url}
                          fileName={message.fileName}
                          userId={userId}
                          onRemove={() => {
                            void setFieldValue(message.mediaType === 'image' ? 'image' : 'video', '');
                            if (message.mediaType === 'image') {
                              setFieldValue('image', '');
                            } else {
                              setFieldValue('video', '');
                            }
                            setMessages(prev =>
                              prev.map(m =>
                                m.role === 'user' && m.id === message.id && m.type === 'media'
                                  ? { id: m.id, role: m.role, type: 'text', text: _t('buttons.skip') }
                                  : m
                              )
                            );
                          }}
                          onReupload={(url: string, fileName: string) => {
                            const field = message.mediaType === 'image' ? 'image' : 'video';
                            setFieldValue(field, url);

                            setMessages(prev =>
                              prev.map(m =>
                                m.role === 'user' && m.id === message.id && m.type === 'media'
                                  ? { ...m, url: url, fileName }
                                  : m
                              )
                            );
                          }}
                        />
                      );
                    }

                    return <UserTextMessage key={message.id} text={message.text} />;
                  })}

                  {isBotThinking ? (
                    <BotThinkingIndicator />
                  ) : (
                    <BotMessage
                      key={step}
                      text={_t(currentStep.message)}
                      animate
                      onTypingComplete={() => setIsBotTypingDone(true)}
                      onTypingTick={scrollChatToEnd}
                    />
                  )}

                  {step === ChatSteps.loader && (
                    <div className={css.loader}>
                      <Loader color="white" />
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>

                {!isBotBusy && (
                  <div className={css.chatControls}>
                    {currentStep.formikField && currentStep.formikFieldType !== 'media' && (
                      <button
                        type="button"
                        className={css.nextBtn}
                        disabled={isSubmitting}
                        onClick={async () => {
                          const field = currentStep.formikField;

                          if (!field || field === 'media') {
                            return;
                          }

                          const isValid = await validateStep(field, values, setFieldTouched, setFieldError);

                          if (!isValid) {
                            return;
                          }

                          archiveCurrentExchange({
                            type: 'text',
                            text:
                              currentStep.formikFieldType === 'radio'
                                ? _t(`buttons.${values.gender}`)
                                : String(values[field as keyof FormValues] ?? '').trim(),
                          });
                          goNextStep();
                        }}
                      >
                        <Icon type="submit-jet" />
                      </button>
                    )}

                    {currentStep.formikField &&
                      currentStep.formikFieldType &&
                      renderFormikField(currentStep.formikFieldType, setFieldValue, values, isBotBusy)}

                    {step === ChatSteps.confirmation && (
                      <button type="button" className={css.confirmBtn} onClick={() => submitForm()}>
                        {_t('buttons.confirm')}
                      </button>
                    )}
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </div>
  );
}
